// WizardSourceSection — US-126/127 (lead source picker + source forms).
//
// Extracted verbatim from CreateWizard's inline "Section 3 — source" block.
// `accountName` replaces the inline `account?.name ?? ''` expression the
// source block used in two places (locked tooltip + Sales-Nav hint).

import { type CampaignSourceType } from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { Banner, Card, Pill } from '@barel/ui';

import { LinkedinUrlSourceForm } from '../sources/LinkedinUrlSourceForm';
import { SalesNavSourceForm } from '../sources/SalesNavSourceForm';
import { CSVImportDropzone, type CSVImportResult } from '../sources/CSVImportDropzone';

export interface WizardSourceSectionProps {
  sourceType: CampaignSourceType;
  onSourceTypeChange: (next: CampaignSourceType) => void;
  hasSalesNav: boolean;
  accountName: string;
  sourceUrl: string;
  onSourceUrlChange: (next: string) => void;
  csvResult: CSVImportResult | null;
  onCsvResultChange: (next: CSVImportResult | null) => void;
}

export function WizardSourceSection({
  sourceType,
  onSourceTypeChange,
  hasSalesNav,
  accountName,
  sourceUrl,
  onSourceUrlChange,
  csvResult,
  onCsvResultChange,
}: WizardSourceSectionProps) {
  const t = useT('campaigns');
  return (
    <Card padding="lg" radius="2xl" data-testid="wizard-section-source">
      <div className="mb-3">
        <strong className="text-primary text-sm">{t('create.sectionSource.title')}</strong>
        <p className="text-tertiary text-xs mt-1">
          {hasSalesNav
            ? t('create.sectionSource.subWithSn')
            : t('create.sectionSource.subWithoutSn')}
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
        {[
          {
            id: 'salesnav' as const,
            icon: '🧭',
            label: t('create.sectionSource.salesnavLabel') as string,
            desc: t('create.sectionSource.salesnavDesc') as string,
            disabled: !hasSalesNav,
          },
          {
            id: 'linkedin' as const,
            icon: '🔍',
            label: t('create.sectionSource.linkedinLabel') as string,
            desc: t('create.sectionSource.linkedinDesc') as string,
          },
          {
            id: 'csv' as const,
            icon: '📄',
            label: t('create.sectionSource.csvLabel') as string,
            desc: t('create.sectionSource.csvDesc') as string,
          },
        ].map((opt) => {
          const active = sourceType === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => !opt.disabled && onSourceTypeChange(opt.id)}
              disabled={opt.disabled}
              aria-pressed={active}
              data-testid={`wizard-source-${opt.id}`}
              className="flex flex-col items-start gap-1 rounded-2xl px-4 py-3 text-left disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                background: active ? 'var(--accent-dim)' : 'var(--bg-surface)',
                border: active ? '1.5px solid var(--accent-default)' : '1.5px solid var(--border)',
                position: 'relative',
              }}
              title={
                opt.disabled
                  ? (t('create.sectionSource.lockedTooltip', {
                      accountName,
                    }) as string)
                  : undefined
              }
            >
              <span aria-hidden="true" className="text-lg">
                {opt.icon}
              </span>
              <strong className="text-primary text-sm">{opt.label}</strong>
              <span className="text-tertiary text-xs">{opt.desc}</span>
              {opt.disabled ? (
                <Pill tone="neutral" size="xs" style={{ position: 'absolute', top: 8, right: 8 }}>
                  {t('create.sectionSource.lockedBadge')}
                </Pill>
              ) : null}
            </button>
          );
        })}
      </div>
      {!hasSalesNav ? (
        <Banner tone="info" variant="soft" className="mb-3" data-testid="wizard-source-hint">
          {t('create.sectionSource.snHint', { accountName })}
        </Banner>
      ) : null}

      {sourceType === 'salesnav' ? (
        <SalesNavSourceForm value={sourceUrl} onChange={onSourceUrlChange} />
      ) : null}
      {sourceType === 'linkedin' ? (
        <LinkedinUrlSourceForm value={sourceUrl} onChange={onSourceUrlChange} />
      ) : null}
      {sourceType === 'csv' ? (
        <CSVImportDropzone
          campaignId="draft"
          initial={csvResult}
          onImported={onCsvResultChange}
          onClear={() => onCsvResultChange(null)}
        />
      ) : null}
    </Card>
  );
}
